import type { AuthError, AuthResult, HumanStatusResponse } from '../../sessionClient';
import type { AccountFormStep } from '../AccountFormContext';
import { humanStatusToStep } from './humanStatus';

/**
 * Failure signal for a `register` callback that has already handled its own
 * failure by advancing the step: it stops `RegistrationModule` completing the
 * flow behind the step change. It carries no user-facing message — the new
 * screen is the feedback — and the module neither renders nor reports it, so a
 * handled recovery never reads as a failure.
 *
 * Thrown by `routeRegistrationEmailConflict`, and only ever after a step change
 * has actually happened. Don't construct it: throwing it without having moved
 * the user is how a screen goes silent with nothing on it.
 */
export class RegistrationRoutedError extends Error {
  constructor() {
    super('registration routed to another step');
    this.name = 'RegistrationRoutedError';
  }
}

/**
 * Whether a failed register is "that address already has an account". Rails
 * reports it as a 422 carrying an `email` field error, which `sessionClient`
 * types as `validation-failed` plus `fieldErrors.email`. Keyed on the code and
 * the field name and never on the message text — that string is raw Rails copy,
 * and this decides whether a user gets rerouted.
 *
 * Deliberately broader than uniqueness: every `:email` validation Rails runs
 * lands in the same bucket, so a rejected format or domain would match too. Today
 * only uniqueness reaches it, because Rails' other two email rules (presence and
 * `EMAIL_REGEX` format, `human.rb:248,257`) are already gated client-side by
 * `registrationSchema`'s `z.email()`. **This is the place to tighten if a
 * server-only email rule ever lands** — a blocked or disposable-domain list —
 * because `routeRegistrationEmailConflict` cannot tell such a rejection from a
 * genuine conflict, and its lookup-failure fallback assumes an account exists.
 */
export function isRegistrationEmailConflict(error: AuthError): boolean {
  return error.code === 'validation-failed' && Boolean(error.fieldErrors?.email);
}

/**
 * Where a taken address goes when the status re-check itself fails. The password
 * step, because it is the only guess recoverable from either kind of account: it
 * is a read, nothing is mailed on arrival, and it offers both escapes — "Forgot
 * your password?" and "Email a login link" — so a generated-password human with
 * no password to type is one click from the magic link. Routing to `magic-link`
 * instead would mail a link nobody asked for, over the same network that just
 * failed the lookup, and then render a send error under a "Check your email"
 * heading.
 *
 * Note this answers "which kind of account", not "is there one at all" — see the
 * caveat on `isRegistrationEmailConflict`.
 */
const LOOKUP_FAILURE_STEP: AccountFormStep = 'password';

/**
 * Re-runs the human-status lookup for an address `POST /register` rejected as
 * taken and advances to the sign-in step it implies — the routing the entry step
 * does, re-run for an address the entry step never saw, because the registration
 * screen's email field is editable.
 *
 * **Throws `RegistrationRoutedError` when it routes**, so the caller cannot
 * advance the step and then complete the flow anyway: that would fire the
 * marketing-consent write and `onSuccess` for an account this flow did not
 * create. Returning normally means it did *not* route — the lookup says the
 * address is still new, so the rejection was not a uniqueness failure after all
 * — and the caller must then surface the server's own error. Routing in that
 * case would set the step already on screen, which is no re-render and no
 * unmount, leaving the user with a submit button that spins down onto no message
 * at all, worse than the raw server string. So the shape at every call site is:
 * `await routeRegistrationEmailConflict(...)` followed by the caller's own
 * failure throw.
 *
 * Pass the address that was actually submitted, never the shared context
 * `email`: a `register` callback closed over that value holds the pre-edit one,
 * which is precisely the address this is not about.
 *
 * The caller's step graph owes `'password'` and `'magic-link'` a screen;
 * `AccountFormV2` routes both.
 */
export async function routeRegistrationEmailConflict({
  email,
  requestHumanStatus,
  setStep,
}: {
  email: string;
  requestHumanStatus: (email: string) => Promise<AuthResult<HumanStatusResponse>>;
  setStep: (step: AccountFormStep) => void;
}): Promise<void> {
  const statusResult = await requestHumanStatus(email);
  if (!statusResult.success) {
    setStep(LOOKUP_FAILURE_STEP);
    throw new RegistrationRoutedError();
  }
  const step = humanStatusToStep(statusResult.data);
  if (step === 'new') {
    return;
  }
  setStep(step);
  throw new RegistrationRoutedError();
}
